/**
 * CoPilot Structured Logger
 *
 * Single entry point for every log line in the CoPilot Guide + hero
 * pages. Replaces the 123 ad-hoc `console.log('[CP:...]', {...})`
 * calls scattered across the suite (radar 2026-04-28 — QA Logging
 * Audit). The audit found:
 *   • 117/123 calls had no env guard → production DevTools noise.
 *   • No correlation-id linking related events across files.
 *   • Mixed shapes (some objects, some interpolated strings).
 *   • Sentry/PostHog wired but underused — observability infra was
 *     ready, the suite just didn't compose against it.
 *
 * What this logger does:
 *
 *   1. **Console output** is gated by env (dev only by default; prod
 *      can opt in via `VITE_COPILOT_DEBUG=true` for staging triage).
 *      Each line carries a stable `[CP:<scope>] <event>` prefix that
 *      matches the existing tagging convention so existing DevTools
 *      filters keep working.
 *
 *   2. **Sentry breadcrumb** added on EVERY call (level-mapped). When
 *      a real error fires later, the breadcrumb chain is the cleanest
 *      reproduction trail QA gets — better than scrolling 200 lines
 *      of disparate console output.
 *
 *   3. **Sentry exception** captured on `.error()` calls, with the
 *      structured context attached so the issue page in Sentry has
 *      `flowId`, `journeyId`, `userId`, `correlationId` already
 *      tagged — no copy-paste from logs needed.
 *
 *   4. **PostHog analytics event** fired on calls marked as
 *      `analyticEvent: true`. Routes through the same redaction +
 *      Zod-typed registry as the rest of the platform (we DO NOT
 *      bypass the registry — analytic-eligible events must register
 *      a name in `EventRegistry`).
 *
 *   5. **PII redaction** is applied uniformly — `sanitizeEventProps`
 *      from `@tonnie/observability` strips `password`, `token`,
 *      `apiKey`, etc. + scrubs emails / phones / SSNs from string
 *      values before they hit any sink.
 *
 *   6. **Correlation context** — every log carries the active
 *      `correlationId` (read from React context populated at the
 *      start of a flow) so a QA engineer can grep one ID and see
 *      every event that touched the user's session — frontend logs,
 *      Sentry breadcrumbs, platform-api logs (via the SDK
 *      interceptor that propagates the same id).
 *
 * The logger is intentionally framework-agnostic at construction time
 * — `createCopilotLogger` returns a plain object so it can be used
 * outside React hooks (utility code, engine), and the React-flavored
 * `useCopilotLogger` thin-wraps it for hook call sites.
 */

import type { IErrorClient } from '@tonnie/observability';
import { sanitizeEventProps } from '@tonnie/observability';

// ─── Types ───────────────────────────────────────────────────────────

/**
 * Stable scope prefix. Keeping the union small forces consistency —
 * adding a new scope requires editing this file, which serves as a
 * documentation pin. The values mirror the ad-hoc tags used pre-2026-
 * 04-28 (`[CP:Engine]`, `[CP:Manager]`, …) so existing DevTools
 * filters keep working.
 */
export type CopilotLogScope =
  | 'CP:Engine' // useCoPilotGuide
  | 'CP:Save' // useGuideSession
  | 'CP:LS' // localStorage layer
  | 'CP:Manager' // FlowLifecycleManager
  | 'CP:Finalize' // useGuideHostFinalize
  | 'CP:LeaveGuard' // useFlowLeaveGuard
  | 'CP:Widget' // useWidgetState
  | 'CP:Journey' // JourneyInstanceContext
  | 'CP:Modal' // RetakeConfirmModal / RestartConfirmModal / lifecycle-modals
  | 'CP:Host:CheckUp'
  | 'CP:Host:Care'
  | 'CP:Host:QultureUp'
  | 'CP:Host:TelePortal'
  | 'CP:Host:JourneyBuilder';

export type CopilotLogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Stable event identifiers per scope. Free-form strings are tolerated
 * (TypeScript allows any string here) but reviewers should prefer the
 * documented set in `KNOWN_EVENTS` below — stable identifiers keep
 * QA dashboards searchable.
 *
 * Examples:
 *   • CP:Engine        → 'instantiate' | 'mount' | 'unmount' | 'restore.server.ok' | …
 *   • CP:Save          → 'fire' | 'skip.unchanged' | 'server.ok' | 'server.failed' | 'schedule'
 *   • CP:Manager       → 'mount' | 'unmount' | 'restart.start' | 'restart.ok' | 'leave.prompt'
 *
 * Event names use lowercase dot-separated tokens. Matches Datadog /
 * Honeycomb conventions.
 */
export type CopilotLogEvent = string;

/**
 * Context attached to every log line. Drives both the structured
 * payload (Sentry tags + breadcrumbs + analytics) and the human-
 * readable line in DevTools.
 *
 * `correlationId` is the spine — it's the id stamped on the
 * outbound `x-trace-id` header when the SDK calls platform-api, so
 * the same id appears in backend logs for the same user action.
 */
export interface CopilotLogContext {
  flowId?: string;
  journeyId?: string | null;
  tenantId?: string;
  userId?: string;
  /**
   * Server-issued chat id within the flow. When present, lets QA
   * filter logs to a single conversation step.
   */
  chatId?: string;
  /** Active widget name when relevant (e.g. 'video_carousel'). */
  widgetName?: string;
  /**
   * Correlation id — propagated to platform-api via the SDK
   * interceptor's `x-trace-id` header. Same value appears in
   * backend logs and Sentry events. Spine of cross-service tracing.
   */
  correlationId?: string;
  /**
   * Free-form structured data. Sanitised before any sink — no
   * password / token fields will reach Sentry or PostHog. Avoid
   * raw user input here; prefer summarised metadata.
   */
  data?: Record<string, unknown>;
}

interface LogCallOptions {
  /**
   * When true, the call is also fired as a typed PostHog analytics
   * event under `event` (must be registered in `EventRegistry`).
   * Defaults to false — analytics events are explicit, not implicit.
   */
  analytic?: boolean;
  /**
   * Optional Error to attach to the Sentry capture (only meaningful
   * for `.error()` calls). When omitted on `.error()`, a synthetic
   * Error is constructed from the event name so the Sentry stack
   * still points back to the call site.
   */
  error?: Error;
}

export interface CopilotLogger {
  debug(event: CopilotLogEvent, ctx?: CopilotLogContext, options?: LogCallOptions): void;
  info(event: CopilotLogEvent, ctx?: CopilotLogContext, options?: LogCallOptions): void;
  warn(event: CopilotLogEvent, ctx?: CopilotLogContext, options?: LogCallOptions): void;
  error(event: CopilotLogEvent, ctx?: CopilotLogContext, options?: LogCallOptions): void;
  /**
   * Fork the logger with additional default context. Returned logger
   * merges the parent's context with the child's on every call. Used
   * to bind flowId/journeyId once per host page.
   */
  child(ctx: CopilotLogContext): CopilotLogger;
}

// ─── Console gating ──────────────────────────────────────────────────

/**
 * Controls console output. Defaults to dev-only; staging triage can
 * opt in via `VITE_COPILOT_DEBUG=true` to see the full stream
 * temporarily.
 */
function consoleGated(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env?.DEV) return true;
  // String comparison — Vite injects env values as strings.
  return import.meta.env?.VITE_COPILOT_DEBUG === 'true';
}

// ─── Sinks ───────────────────────────────────────────────────────────

interface LoggerSinks {
  /** Optional error client (Sentry). Logger no-ops the sink when missing. */
  errorClient?: IErrorClient;
  /**
   * PostHog `track` function. Logger no-ops the sink when missing.
   * Typed sparsely on purpose — the EventRegistry enforces shape at
   * the analytics-client layer, not here.
   */
  trackEvent?: (eventName: string, properties: Record<string, unknown>) => void;
}

// ─── Implementation ──────────────────────────────────────────────────

const SCOPE_TO_BREADCRUMB_CATEGORY: Record<CopilotLogScope, string> = {
  'CP:Engine': 'copilot.engine',
  'CP:Save': 'copilot.save',
  'CP:LS': 'copilot.localStorage',
  'CP:Manager': 'copilot.manager',
  'CP:Finalize': 'copilot.finalize',
  'CP:LeaveGuard': 'copilot.leave-guard',
  'CP:Widget': 'copilot.widget',
  'CP:Journey': 'copilot.journey',
  'CP:Modal': 'copilot.modal',
  'CP:Host:CheckUp': 'copilot.host.checkup',
  'CP:Host:Care': 'copilot.host.care',
  'CP:Host:QultureUp': 'copilot.host.qultureup',
  'CP:Host:TelePortal': 'copilot.host.teleportal',
  'CP:Host:JourneyBuilder': 'copilot.host.journey-builder',
};

const LEVEL_TO_SENTRY_LEVEL: Record<
  CopilotLogLevel,
  'debug' | 'info' | 'warning' | 'error' | 'fatal'
> = {
  debug: 'debug',
  info: 'info',
  warn: 'warning',
  error: 'error',
};

function emitConsole(
  level: CopilotLogLevel,
  scope: CopilotLogScope,
  event: CopilotLogEvent,
  payload: Record<string, unknown>
): void {
  if (!consoleGated()) return;
  const tag = `[${scope}] ${event}`;

  const fn =
    level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : level === 'debug'
          ? console.debug
          : console.log;
  // Single-arg payload keeps DevTools' "expand inline" UX clean.
  fn(tag, payload);
}

export function createCopilotLogger(args: {
  scope: CopilotLogScope;
  defaultContext?: CopilotLogContext;
  sinks?: LoggerSinks;
}): CopilotLogger {
  const { scope, defaultContext = {}, sinks = {} } = args;

  const emit = (
    level: CopilotLogLevel,
    event: CopilotLogEvent,
    ctx?: CopilotLogContext,
    options?: LogCallOptions
  ) => {
    const merged: CopilotLogContext = { ...defaultContext, ...ctx };
    const data = ctx?.data ?? defaultContext.data ?? undefined;

    // Compose the structured payload that goes to all sinks.
    // PII / sensitive keys are redacted at the sink boundary so a
    // bug in a caller never leaks tokens to Sentry or PostHog.
    const rawPayload: Record<string, unknown> = {
      flowId: merged.flowId,
      journeyId: merged.journeyId ?? undefined,
      tenantId: merged.tenantId,
      userId: merged.userId,
      chatId: merged.chatId,
      widgetName: merged.widgetName,
      correlationId: merged.correlationId,
      ...(data ?? {}),
    };
    // Strip undefined values so the console output stays compact.
    const payload = Object.fromEntries(
      Object.entries(rawPayload).filter(([, v]) => v !== undefined)
    );
    const sanitized = sanitizeEventProps(payload);

    // 1. Console (dev / opt-in prod only).
    emitConsole(level, scope, event, sanitized);

    // 2. Sentry message capture for warn-level events. Sentry's SDK
    //    collects these as breadcrumbs automatically when they
    //    precede a `captureException` in the same session — no
    //    manual `addBreadcrumb` call needed (and our IErrorClient
    //    abstraction intentionally doesn't expose Sentry's
    //    breadcrumb API directly). Info / debug events stay console-
    //    only to keep the Sentry quota lean. Scope category mirrors
    //    the breadcrumb conventions for greppability.
    if (level === 'warn' && sinks.errorClient) {
      sinks.errorClient.captureMessage(`[${SCOPE_TO_BREADCRUMB_CATEGORY[scope]}] ${event}`, {
        level: LEVEL_TO_SENTRY_LEVEL[level],
        tags: { component: scope },
        extra: sanitized,
      });
    }

    // 3. Sentry exception on .error() — captures the full stack
    //    with the structured payload as `extra`. Any preceding
    //    `warn` calls in the same session are auto-attached as
    //    breadcrumbs by Sentry.
    if (level === 'error' && sinks.errorClient) {
      const err = options?.error ?? new Error(`[${scope}] ${event}`);
      sinks.errorClient.captureException(err, {
        tags: {
          component: scope,
          flowId: merged.flowId ?? '',
          // Tags are searchable in Sentry; keep them small.
        },
        extra: sanitized,
      });
    }

    // 4. PostHog analytics event when explicitly opted in.
    if (options?.analytic && sinks.trackEvent) {
      sinks.trackEvent(event, sanitized);
    }
  };

  const logger: CopilotLogger = {
    debug: (event, ctx, options) => emit('debug', event, ctx, options),
    info: (event, ctx, options) => emit('info', event, ctx, options),
    warn: (event, ctx, options) => emit('warn', event, ctx, options),
    error: (event, ctx, options) => emit('error', event, ctx, options),
    child: (childCtx) =>
      createCopilotLogger({
        scope,
        defaultContext: { ...defaultContext, ...childCtx },
        sinks,
      }),
  };

  return logger;
}

// ─── Known event vocabulary (documentation only) ─────────────────────

/**
 * Reference list of the stable events QA can rely on. Not enforced at
 * the type level (string is permitted) so authors aren't blocked, but
 * use this set when possible — it's what dashboards / alerts key off.
 *
 * Keep grouped by scope. New entries should match the lowercase
 * dot-separated convention (`subsystem.action.outcome`).
 */
export const KNOWN_EVENTS = {
  'CP:Engine': [
    'instantiate',
    'mount',
    'unmount',
    'restore.waiting',
    'restore.server.ok',
    'restore.server.terminal',
    'restore.server.threw',
    'restore.localStorage.ok',
    'restore.localStorage.threw',
    'restore.fresh-start',
    'action.selected',
  ],
  'CP:Save': ['fire', 'skip.unchanged', 'server.ok', 'server.failed', 'schedule'],
  'CP:LS': ['save.ok', 'save.failed', 'load.hit', 'load.miss', 'load.expired', 'clear'],
  'CP:Manager': [
    'mount',
    'unmount',
    'guide-state.changed',
    'restart.start',
    'restart.skipped.in-flight',
    'restart.ok',
    'restart.failed',
    'discard.start',
    'discard.skipped.in-flight',
    'discard.ok',
    'discard.failed',
    'leave.prompt',
    'leave.resolve',
    'leave.flush.failed',
    'switch-guard.registered',
    'switch-guard.unregistered',
    'switch-guard.resolved',
  ],
  'CP:Finalize': [
    'start',
    'server.ok',
    'after-finalize.start',
    'after-finalize.ok',
    'after-finalize.failed',
    'retry',
    'done',
  ],
  'CP:LeaveGuard': ['callback.threw'],
  'CP:Widget': ['hydrate', 'patch.queued', 'patch.flushed', 'patch.failed', 'patch.retry'],
  'CP:Modal': ['open', 'confirm', 'cancel', 'submit.ok', 'submit.failed'],
} as const;
